/**
 * Company: SPHMedia
 * Description: [Inline video player component for portrait videos]
 */

import MuteButton from "@assets/actions/Mute.png";
import PlayIcon from "@assets/actions/play-icon.png";
import UnMuteButton from "@assets/actions/Sound.png";
import { BrightcoveVideo } from "@blocks/BrightcoveVideo/BrightcoveVideo";
import stickyController from "@helper/stickyController";
import type {
  LatestVideoResponse,
  VideoPlayerRef,
} from "@typings/Brightcove.d";
import {
  Dispatch,
  ReactElement,
  RefObject,
  SetStateAction,
  useRef,
} from "react";

import useInlineVideoPlayer from "./hooks/useInlineVideoPlayer";
import useStickyInlinePlayer from "./hooks/useStickyInlinePlayer";

type InlineVideoPlayerProps = {
  playing: LatestVideoResponse;
  forwardedRef: RefObject<HTMLDivElement | null>;
  observerRef: RefObject<HTMLDivElement | null>;
  pipContainerRef: RefObject<HTMLDivElement | null>;
  onEnded?: () => void;
  nextPlaylist?: () => void;
  pageRefValue?: string;
  isMute: boolean;
  setIsMute: Dispatch<SetStateAction<boolean>>;
  isSticky: boolean;
  setIsSticky: Dispatch<SetStateAction<boolean>>;
};

/**
 * InlineVideoPlayer
 * Description: [Description of what the component does]
 * @param props - The props required by the component (if any)
 * @returns The rendered React element
 */
export default function InlineVideoPlayer(
  props: InlineVideoPlayerProps,
): ReactElement {
  const {
    playing,
    forwardedRef,
    onEnded,
    nextPlaylist,
    pageRefValue = "tm_shorts",
    observerRef,
    pipContainerRef,
    isMute,
    setIsMute,
    isSticky,
    setIsSticky,
  } = props;
  const { id } = playing;
  const inlineContainerRef = useRef<HTMLDivElement | null>(null);
  const playerWrapperRef = useRef<HTMLDivElement | null>(null);
  const playerRef = useRef<VideoPlayerRef | null>(null);
  const playerId = "inline-video-player";
  const {
    isPlaying,
    toggleMute,
    togglePlay,
    handlePlayerReady,
    handlePlayerExpand,
  } = useInlineVideoPlayer({ playerRef, pageRefValue, setIsMute });
  useStickyInlinePlayer({
    pipContainerRef,
    inlineContainerRef,
    playerWrapperRef,
    observerRef,
    playerId,
    isMute,
    isPlaying,
    isSticky,
    setIsSticky,
  });

  return (
    <>
      <div ref={inlineContainerRef} className="min-h-[430px]">
        <div
          ref={playerWrapperRef}
          className="flex flex-col mx-auto bg-white-100 transition-all duration-300"
        >
          <div className="relative">
            <div className="w-full">
              <BrightcoveVideo
                videoId={id}
                forwardedRef={forwardedRef}
                onEnded={onEnded}
                nextPlaylist={nextPlaylist}
                className={"!aspect-[230/430]"}
                onPlayerReady={handlePlayerReady}
              />
            </div>

            {isSticky && (
              <div className="z-20 absolute top-0 right-0 flex items-center justify-center w-8 h-8.4 p-1 rounded-full bg-gray-2150">
                <button
                  type="button"
                  aria-label="Close picture-in-picture"
                  onClick={() => {
                    setIsSticky(false);
                    stickyController.clear(playerId);
                    playerRef.current?.pause();
                  }}
                  className="text-white-100 text-base outline-none focus:outline-none"
                >
                  X
                </button>
              </div>
            )}

            <div
              className="absolute inset-0 w-full h-full z-10"
              style={{
                background:
                  "linear-gradient(180deg, rgba(0,0,0,0) 80%, rgba(0,0,0,0.8) 100%)",
              }}
            >
              <button
                type="button"
                onClick={togglePlay}
                className="w-full h-full"
                aria-label="Toggle Play"
              />
            </div>

            {!isPlaying && (
              <div className="absolute inset-0 pointer-events-none flex items-center justify-center transition-opacity duration-300 opacity-100">
                <img src={PlayIcon} alt="Play Video" width={40} height={40} />
              </div>
            )}

            <div className="absolute bottom-0 z-30 w-full bg-gradient-to-t from-black-100 to-transparent p-1">
              <div className="flex items-center justify-between p-2">
                {/* Mute/Unmute */}
                <button type="button" onClick={toggleMute} className="flex">
                  <img
                    src={isMute ? MuteButton : UnMuteButton}
                    alt={isMute ? "Mute" : "Unmute"}
                    width={18}
                    height={17}
                  />
                </button>

                {/* Expand */}
                <button
                  type="button"
                  onClick={handlePlayerExpand}
                  className="flex"
                >
                  <span className="before:content-['\f108'] font-['VideoJS'] text-sm text-white-100 leading-[100%]"></span>
                  <span className="sr-only">Expand</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
